import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';
import { Divider } from '@mui/material';

import { faqCategories } from './faqData';

interface OpenStates {
  [key: string]: boolean;
}

const FaqDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [openCategories, setOpenCategories] = useState<OpenStates>({});
  const [openQuestions, setOpenQuestions] = useState<OpenStates>({});

  const toggleCategory = (categoryId: string) => {
    setOpenCategories((prev) => ({ ...prev, [categoryId]: !prev[categoryId] }));
  };

  const toggleQuestion = (questionKey: string) => {
    setOpenQuestions((prev) => ({ ...prev, [questionKey]: !prev[questionKey] }));
  };

  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return faqCategories;

    const query = searchQuery.toLowerCase();
    return faqCategories
    .map((category) => {
      const filteredQuestions = category.questions.filter(
          (q) =>
              t(q.questionKey).toLowerCase().includes(query) ||
              t(q.answerKey).toLowerCase().includes(query)
      );

      return { ...category, questions: filteredQuestions };
    })
    .filter(
        (category) =>
            category.questions.length > 0 ||
            t(category.titleKey).toLowerCase().includes(query)
    );
  }, [searchQuery, t]);

  return (
      <div className="w-full h-[calc(100vh-90px)] md:h-[calc(100vh-50px)] bg-light dark:bg-dark text-light-text dark:text-dark-text pt-6 md:pt-10 pb-4 px-4 md:px-6 flex flex-col overflow-hidden">
        <div className="flex flex-col sm:py-4 sm:px-5 py-4 px-2 space-y-4 bg-white dark:bg-neutral-900/40 shadow-sm border border-gray-100 dark:border-white/5 rounded-xl flex-1 overflow-hidden">

          {/* Header and Search Bar */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 shrink-0">
            <div className="flex items-center gap-2.5">
              <HelpOutlineIcon className="text-gray-400 dark:text-gray-500" sx={{ fontSize: 24 }} />
              <div>
                <h1 className="text-lg font-black uppercase tracking-[0.1em] text-gray-800 dark:text-white">
                  {t('faq.header.title')}
                </h1>
                <p className="text-[10px] text-gray-400 tracking-wider uppercase mt-0.5">
                  {t('faq.header.subtitle')}
                </p>
              </div>
            </div>

            <div className="relative w-full md:w-72">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-neutral-500" fontSize="small" />
              <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('faq.search.placeholder')}
                  className="w-full pl-9 pr-4 py-1.5 text-xs bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 dark:focus:border-dark-primary transition-colors duration-200"
              />
            </div>
          </div>

          <Divider className="!border-gray-100 dark:!border-white/5 shrink-0" />

          {/* Categories Accordion Stack */}
          <div className="flex-1 overflow-y-auto pr-2 space-y-2.5">
            {filteredCategories.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 opacity-30">
                  <HelpOutlineIcon sx={{ fontSize: 56 }} className="text-gray-300 dark:text-gray-600 mb-3" />
                  <p className="text-sm font-black text-gray-400 uppercase tracking-[0.2em]">
                    {t('faq.empty.search')}
                  </p>
                </div>
            ) : (
                filteredCategories.map((category) => {
                  const isCategoryOpen = openCategories[category.id] || searchQuery.length > 0;
                  const Icon = category.icon;

                  return (
                      <div
                          key={category.id}
                          className="border border-neutral-100 dark:border-white/5 rounded-lg bg-neutral-50/50 dark:bg-neutral-900/20 overflow-hidden"
                      >
                        {/* Category Trigger Header */}
                        <button
                            onClick={() => toggleCategory(category.id)}
                            className="w-full flex items-center justify-between px-4 py-2.5 bg-white dark:bg-neutral-900 hover:bg-gray-50 dark:hover:bg-neutral-800/60 transition-colors duration-200 text-left"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-md bg-blue-50 dark:bg-neutral-800 flex items-center justify-center text-light-primary dark:text-dark-primary">
                              <Icon sx={{ fontSize: 18 }} />
                            </div>
                            <div>
                              <h3 className="text-sm font-bold text-gray-800 dark:text-neutral-200">
                                {t(category.titleKey)}
                              </h3>
                              <p className="text-[10px] text-gray-400 mt-0.5">
                                {category.questions.length} {category.questions.length === 1 ? t('faq.unit.question') : t('faq.unit.questions')}
                              </p>
                            </div>
                          </div>
                          <KeyboardArrowDownIcon
                              fontSize="small"
                              className={`text-gray-400 transition-transform duration-300 ${isCategoryOpen ? 'rotate-180' : ''}`}
                          />
                        </button>

                        {/* Questions Wrapper Content */}
                        {/* Nutzt jetzt ein CSS-Grid zur verzögerungsfreien Höhen-Animation */}
                        <div
                            className={`grid transition-[grid-template-rows] duration-300 ease-in-out border-neutral-100 dark:border-white/5 ${
                                isCategoryOpen ? 'grid-rows-[1fr] border-t' : 'grid-rows-[0fr]'
                            }`}
                        >
                          <div className="overflow-hidden min-h-0 bg-white/40 dark:bg-neutral-950/20">
                            <div className="p-2.5 space-y-2">
                              {category.questions.map((item) => {
                                const isQuestionOpen = openQuestions[item.questionKey];

                                return (
                                    <div
                                        key={item.questionKey}
                                        className="border border-neutral-200/60 dark:border-neutral-800/60 rounded-md overflow-hidden bg-white dark:bg-neutral-900"
                                    >
                                      {/* Question Row Click */}
                                      <button
                                          onClick={() => toggleQuestion(item.questionKey)}
                                          className="w-full flex items-center justify-between px-3.5 py-2 hover:bg-gray-50/80 dark:hover:bg-neutral-800/40 transition-colors duration-200 text-left"
                                      >
                                        <span className="text-xs font-bold tracking-wide text-gray-700 dark:text-neutral-300">
                                          {t(item.questionKey)}
                                        </span>
                                        <KeyboardArrowDownIcon
                                            fontSize="small"
                                            className={`text-gray-400 shrink-0 ml-3 transition-transform duration-200 ${isQuestionOpen ? 'rotate-180' : ''}`}
                                        />
                                      </button>

                                      {/* Answer Area Panel */}
                                      {/* Verwendet ebenfalls das CSS-Grid Prinzip für absolut flüssiges Zuklappen */}
                                      <div
                                          className={`grid transition-[grid-template-rows] duration-200 ease-in-out border-neutral-100 dark:border-neutral-800/60 ${
                                              isQuestionOpen ? 'grid-rows-[1fr] border-t' : 'grid-rows-[0fr]'
                                          }`}
                                      >
                                        <div className="overflow-hidden min-h-0">
                                          <div className="p-3 text-xs text-gray-600 dark:text-neutral-400 leading-relaxed bg-neutral-50/30 dark:bg-neutral-950/10">
                                            {t(item.answerKey)}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                  );
                })
            )}
          </div>
        </div>
      </div>
  );
};

export default FaqDashboard;